// Content for the Compteurs d'eau ATNA « La solution » section.
// All figures are illustrative demo data, shown only inside the product visuals.
export type Bi = { fr: string; en: string }

export const WATER_SOLUTION_IMAGES = {
  background: 'https://cdn.magicpatterns.com/patterns/generated-images/b06aab60-2393-4079-a664-78a8b6936b46.jpg',
  meter: 'https://cdn.magicpatterns.com/patterns/generated-images/58d48db4-fc00-4933-8ba2-02b4252b18b6.jpg',
}

export const waterSolutionCopy = {
  eyebrow: { fr: 'La solution', en: 'The solution' },
  navy: [
    { fr: 'De la lecture', en: 'From meter' },
    { fr: 'du compteur', en: 'reading' },
  ],
  indigo: { fr: 'à l’action.', en: 'to action.' },
  body: {
    fr: 'BCITI | ATNA capte la donnée, la rend compréhensible, détecte ce qui mérite l’attention et orchestre les prochaines actions.',
    en: 'BCITI | ATNA captures the data, makes it understandable, detects what deserves attention and orchestrates the next actions.',
  },
  meterAlt: { fr: 'Compteur d’eau résidentiel', en: 'Residential water meter' },
}

export const dataSources = {
  title: { fr: 'Sources de données', en: 'Data sources' },
  items: [
    { key: 'iot', title: { fr: 'Capteurs / IoT', en: 'Sensors / IoT' }, sub: { fr: 'Lecture automatique', en: 'Automatic reading' } },
    { key: 'photo', title: { fr: 'Photo du compteur', en: 'Meter photo' }, sub: { fr: 'Reconnaissance d’image (IA)', en: 'Image recognition (AI)' } },
    { key: 'api', title: { fr: 'Intégrations existantes', en: 'Existing integrations' }, sub: { fr: 'Sensus, Neptune, etc.', en: 'Sensus, Neptune, etc.' } },
  ] as { key: 'iot' | 'photo' | 'api'; title: Bi; sub: Bi }[],
}

export const floatingInsight = {
  value: { fr: '1 840 L aujourd’hui', en: '1,840 L today' },
  trend: { fr: '+32 % par rapport à la moyenne', en: '+32% vs average' },
  label: { fr: 'Suggestion ATNA', en: 'ATNA suggestion' },
  text: { fr: 'Vérifier l’arrosage extérieur', en: 'Check outdoor watering' },
}

export const propertyAlert = {
  title: { fr: 'Consommation inhabituelle', en: 'Unusual consumption' },
  lines: [
    { fr: 'Propriété liée et vérifiée', en: 'Linked and verified property' },
    { fr: '123 rue Principale', en: '123 Main Street' },
  ],
}

export const phoneUi = {
  title: { fr: 'Ma consommation', en: 'My consumption' },
  tabs: [
    { fr: 'Jour', en: 'Day' },
    { fr: 'Semaine', en: 'Week' },
    { fr: 'Mois', en: 'Month' },
    { fr: 'Année', en: 'Year' },
  ],
  value: { fr: '1 840 L', en: '1,840 L' },
  trend: { fr: '+32 %', en: '+32%' },
  bars: [40, 62, 50, 74, 58, 88, 70, 96],
  alertTitle: { fr: 'Consommation inhabituelle détectée', en: 'Unusual consumption detected' },
  alertText: { fr: 'Votre consommation est plus élevée que d’habitude.', en: 'Your consumption is higher than usual.' },
  tipTitle: { fr: 'Suggestion', en: 'Suggestion' },
  tipText: { fr: 'Vérifiez l’arrosage extérieur.', en: 'Check outdoor watering.' },
  cta: { fr: 'Activer les notifications', en: 'Turn on notifications' },
}

export const solutionDashboard = {
  search: { fr: 'Rechercher une adresse, un compteur, un usager…', en: 'Search by address, meter or user…' },
  org: { fr: 'Municipalité de Casselman', en: 'Municipality of Casselman' },
  nav: [
    { key: 'overview', label: { fr: 'Aperçu', en: 'Overview' } },
    { key: 'properties', label: { fr: 'Propriétés', en: 'Properties' } },
    { key: 'meters', label: { fr: 'Compteurs', en: 'Meters' } },
    { key: 'consumption', label: { fr: 'Consommation', en: 'Consumption' } },
    { key: 'alerts', label: { fr: 'Alertes', en: 'Alerts' } },
    { key: 'users', label: { fr: 'Usagers', en: 'Users' } },
    { key: 'payments', label: { fr: 'Paiements', en: 'Payments' } },
    { key: 'interventions', label: { fr: 'Interventions', en: 'Work orders' } },
    { key: 'reports', label: { fr: 'Rapports', en: 'Reports' } },
  ] as { key: 'overview' | 'properties' | 'meters' | 'consumption' | 'alerts' | 'users' | 'payments' | 'interventions' | 'reports'; label: Bi }[],
  kpis: [
    { key: 'meters', value: { fr: '12 842', en: '12,842' }, label: { fr: 'Compteurs actifs', en: 'Active meters' }, trend: { fr: '↑ +3 %', en: '↑ +3%' }, tone: 'good' },
    { key: 'high', value: { fr: '127', en: '127' }, label: { fr: 'Consommations élevées', en: 'High consumption' }, trend: { fr: '↑ +32 %', en: '↑ +32%' }, tone: 'bad' },
    { key: 'leak', value: { fr: '18', en: '18' }, label: { fr: 'Fuites potentielles', en: 'Potential leaks' }, trend: null, tone: 'none' },
    { key: 'irrigation', value: { fr: '56', en: '56' }, label: { fr: 'Arrosages prolongés', en: 'Prolonged watering' }, trend: null, tone: 'none' },
  ] as { key: 'meters' | 'high' | 'leak' | 'irrigation'; value: Bi; label: Bi; trend: Bi | null; tone: 'good' | 'bad' | 'none' }[],
  chartTitle: { fr: 'Consommation du territoire', en: 'Town-wide consumption' },
  days: { fr: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'], en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
  values: [120, 190, 205, 215, 260, 265, 300],
  mapTitle: { fr: 'Carte des alertes', en: 'Alert map' },
  mapFilter: { fr: 'Tous les secteurs', en: 'All sectors' },
  markers: [
    { x: 22, y: 28, tone: 'red' },
    { x: 36, y: 42, tone: 'red' },
    { x: 60, y: 20, tone: 'red' },
    { x: 16, y: 66, tone: 'orange' },
    { x: 44, y: 72, tone: 'orange' },
    { x: 66, y: 55, tone: 'orange' },
    { x: 56, y: 86, tone: 'indigo' },
  ] as { x: number; y: number; tone: 'red' | 'orange' | 'indigo' }[],
  actionsTitle: { fr: 'Actions récentes', en: 'Recent actions' },
  cols: { date: { fr: 'Date', en: 'Date' }, property: { fr: 'Propriété', en: 'Property' }, type: { fr: 'Type', en: 'Type' }, status: { fr: 'Statut', en: 'Status' } },
  actions: [
    { date: { fr: 'Aujourd’hui', en: 'Today' }, property: { fr: '123 rue Principale', en: '123 Main St' }, type: { fr: 'Consommation élevée', en: 'High consumption' }, status: { fr: 'Notification envoyée', en: 'Notification sent' }, tone: 'done' },
    { date: { fr: 'Aujourd’hui', en: 'Today' }, property: { fr: '45 rue des Érables', en: '45 Maple St' }, type: { fr: 'Fuite possible', en: 'Possible leak' }, status: { fr: 'Vérification en cours', en: 'Check in progress' }, tone: 'progress' },
    { date: { fr: 'Hier', en: 'Yesterday' }, property: { fr: '78 rue du Lac', en: '78 Lake St' }, type: { fr: 'Paiement autorisé', en: 'Authorized payment' }, status: { fr: 'Exécuté', en: 'Completed' }, tone: 'done' },
  ] as { date: Bi; property: Bi; type: Bi; status: Bi; tone: 'done' | 'progress' }[],
}

export type StageKey = 'capture' | 'display' | 'understand' | 'alert' | 'act'

export const solutionStages: { key: StageKey; label: Bi; text: Bi }[] = [
  { key: 'capture', label: { fr: 'Capter', en: 'Capture' }, text: { fr: 'Capteurs / IoT, prise de photo du compteur ou intégrations existantes.', en: 'Sensors / IoT, meter photo capture or existing integrations.' } },
  { key: 'display', label: { fr: 'Afficher', en: 'Display' }, text: { fr: 'Consommation quotidienne, historique, comparaison par propriété, secteur et territoire.', en: 'Daily consumption, history, comparison by property, sector and territory.' } },
  { key: 'understand', label: { fr: 'Comprendre', en: 'Understand' }, text: { fr: 'ATNA détecte les surconsommations, les fuites possibles et les comportements inhabituels, et dégage des constats concrets.', en: 'ATNA detects overconsumption, possible leaks and unusual patterns, and generates concrete insights.' } },
  { key: 'alert', label: { fr: 'Alerter', en: 'Alert' }, text: { fr: 'Notifications préventives aux bons usagers, rappels réglementaires et messages ciblés.', en: 'Preventive notifications to the right users, by-law reminders and targeted messages.' } },
  { key: 'act', label: { fr: 'Agir', en: 'Act' }, text: { fr: 'Paiement d’un dépassement, création d’une requête, assignation d’une intervention et suivi jusqu’à la résolution.', en: 'Paying an overage, creating a request, assigning a work order and tracking it to resolution.' } },
]

export const stageExtras = {
  capture: [
    { key: 'iot', label: { fr: 'Capteur', en: 'Sensor' } },
    { key: 'photo', label: { fr: 'Photo (IA)', en: 'Photo (AI)' } },
    { key: 'api', label: { fr: 'API', en: 'APIs' } },
  ] as { key: 'iot' | 'photo' | 'api'; label: Bi }[],
  displayBars: [22, 34, 48, 40, 54, 50, 66, 80],
  understand: [
    { key: 'leak', tone: 'red', label: { fr: 'Fuite possible', en: 'Possible leak' } },
    { key: 'over', tone: 'orange', label: { fr: 'Surconsommation', en: 'Overconsumption' } },
    { key: 'unusual', tone: 'indigo', label: { fr: 'Usage inhabituel', en: 'Unusual usage' } },
  ] as { key: 'leak' | 'over' | 'unusual'; tone: 'red' | 'orange' | 'indigo'; label: Bi }[],
  alert: {
    title: { fr: 'Rappel', en: 'Reminder' },
    text: { fr: 'Restriction d’arrosage en vigueur dans votre secteur.', en: 'Watering restriction in effect in your sector.' },
  },
  act: {
    title: { fr: 'Dépassement détecté', en: 'Overage detected' },
    amount: { fr: 'Montant dû : 18,40 $', en: 'Amount due: $18.40' },
    cta: { fr: 'Payer maintenant', en: 'Pay now' },
  },
}
